import { Component, type ErrorInfo, type ReactNode } from 'react'

/**
 * Catches anything that throws during render and puts it on the page.
 *
 * Without this, a throw above the first rendered element leaves `<div id="root">` empty and the
 * browser shows a blank page — no message, no hint, nothing in the network tab, because every asset
 * loaded correctly. That happened here: a missing `QueryClientProvider` produced a perfectly healthy
 * 200 response and a completely white screen.
 *
 * A blank page is the worst failure a site can have, because it looks identical to an outage, a bad
 * deploy, and a bug. Showing the error costs nothing and turns "it's broken" into something
 * actionable.
 */
export class RootErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state: { error: Error | null } = { error: null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    // Still log it: the on-page message is for whoever is looking, the console is for whoever is
    // debugging, and they are rarely the same moment.
    console.error('Unhandled render error', error, info.componentStack)
  }

  render() {
    const { error } = this.state
    if (!error) {
      return this.props.children
    }

    return (
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="text-lg font-semibold text-danger">Something broke while rendering</h1>
        <p className="mt-2 text-sm text-ink-soft">
          The page failed to start. The error is below — it is almost always more useful than a
          blank screen.
        </p>
        <pre className="mt-4 overflow-x-auto rounded border border-line bg-surface p-4 text-xs text-ink">
          {error.message}
        </pre>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded bg-accent-bg px-3 py-1.5 text-sm text-accent hover:bg-surface-raised"
        >
          Reload
        </button>
      </div>
    )
  }
}
